import { useEffect, useState } from 'react';
import { Input } from '@marine/ui/input';
import { centsToDecimalString, parseDecimalToCents } from '../utils/money';

export interface MoneyInputProps {
  value: number | null;
  onChange: (cents: number | null) => void;
  currencyCode?: string;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  /** WCAG 2.1 AA (D14-04, #408) — associates the field with an inline
   * notice below it (e.g. RecordPaymentDialog's overpayment notice) so a
   * screen reader announces it alongside the field's own label. */
  'aria-describedby'?: string;
}

/**
 * A-9 / D6-04 — this component only ever emits and displays integer cents;
 * no Service money value is multiplied or divided in a component, that
 * conversion happens exactly once, in `utils/money.ts`.
 *
 * Holds the raw typed STRING in local state so a partial entry like `"12."`
 * is not destroyed mid-typing. Re-seeds its displayed value only when an
 * externally changed `value` prop no longer matches what the current raw
 * string would itself parse to (an RHF `reset()` on a Sheet reopen) — never
 * on every keystroke, which would fight typing in a genuinely controlled
 * usage where the caller echoes the emitted cents straight back down.
 */
export function MoneyInput({
  value,
  onChange,
  // Accepted for prop-contract parity (a future currency-symbol affordance)
  // and stripped here so it is never spread onto the underlying DOM input —
  // `formatCents(cents, currencyCode)` is what renders the symbol once a
  // value is committed elsewhere in the UI.
  currencyCode: _currencyCode,
  id,
  disabled,
  placeholder,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: MoneyInputProps) {
  const [raw, setRaw] = useState(() => centsToDecimalString(value));

  useEffect(() => {
    if (parseDecimalToCents(raw) !== value) {
      setRaw(centsToDecimalString(value));
    }
    // Intentionally re-runs only when `value` changes — see the doc comment
    // above for why `raw` is excluded from this dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      disabled={disabled}
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder ?? 'Amount'}
      aria-describedby={ariaDescribedBy}
      value={raw}
      onChange={(event) => {
        const next = event.target.value;
        setRaw(next);
        onChange(parseDecimalToCents(next));
      }}
      onBlur={() => {
        setRaw(centsToDecimalString(parseDecimalToCents(raw)));
      }}
    />
  );
}
